import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { Card, Button, Badge, SectionHeader, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

// ── Goethe exam structure per level ──────────────────────────
const GOETHE_STRUCTURE = {
  A1:{
    hoeren:[
      {teil:1,label:'Teil 1 — Kurzgespräche',   type:'true_false',      count:5,  audio:true},
      {teil:2,label:'Teil 2 — Ansagen',          type:'multiple_choice', count:5,  audio:true},
      {teil:3,label:'Teil 3 — Zuordnung',        type:'matching',        count:5,  audio:true},
    ],
    lesen:[
      {teil:1,label:'Teil 1 — Anzeigen',         type:'matching',        count:5,  images:5},
      {teil:2,label:'Teil 2 — Text',             type:'true_false',      count:5,  images:1},
      {teil:3,label:'Teil 3 — Lückentext',       type:'gap_fill',        count:5,  images:1,wordList:true},
    ],
    schreiben:[
      {teil:1,label:'Teil 1 — Formular',         type:'form_fill',       images:1},
      {teil:2,label:'Teil 2 — Kurze Mitteilung', type:'writing',         minW:20,maxW:35},
    ],
  },
  A2:{
    hoeren:[
      {teil:1,label:'Teil 1 — Durchsagen',       type:'multiple_choice', count:4,  audio:true},
      {teil:2,label:'Teil 2 — Radiobeitrag',     type:'true_false',      count:8,  audio:true},
      {teil:3,label:'Teil 3 — Gespräch',         type:'multiple_choice', count:5,  audio:true},
      {teil:4,label:'Teil 4 — Aussagen',         type:'true_false',      count:10, audio:true},
    ],
    lesen:[
      {teil:1,label:'Teil 1 — Kurze Texte',      type:'matching',        count:5,  images:5},
      {teil:2,label:'Teil 2 — Langer Text',      type:'true_false',      count:6,  images:1},
      {teil:3,label:'Teil 3 — Anzeigentafel',    type:'matching',        count:5,  images:1},
      {teil:4,label:'Teil 4 — Lückentext',       type:'gap_fill',        count:5,  images:1,wordList:true},
    ],
    schreiben:[
      {teil:1,label:'Teil 1 — Formular',         type:'form_fill',       images:1},
      {teil:2,label:'Teil 2 — E-Mail/Nachricht', type:'writing',         minW:30,maxW:45},
    ],
  },
  B1:{
    hoeren:[
      {teil:1,label:'Teil 1 — Kurze Meldungen',  type:'true_false',      count:10, audio:true},
      {teil:2,label:'Teil 2 — Interview',        type:'multiple_choice', count:5,  audio:true},
      {teil:3,label:'Teil 3 — Gespräche',        type:'matching',        count:5,  audio:true},
      {teil:4,label:'Teil 4 — Vortrag',          type:'true_false',      count:10, audio:true},
    ],
    lesen:[
      {teil:1,label:'Teil 1 — Überschriften',    type:'matching',        count:5,  images:1},
      {teil:2,label:'Teil 2 — Langer Text',      type:'multiple_choice', count:5,  images:1},
      {teil:3,label:'Teil 3 — Stellenanzeigen',  type:'matching',        count:5,  images:3},
      {teil:4,label:'Teil 4 — Lückentext',       type:'gap_fill',        count:5,  images:1,wordList:true},
    ],
    schreiben:[
      {teil:1,label:'Teil 1 — Forumsbeitrag',    type:'writing',         minW:120,maxW:150},
      {teil:2,label:'Teil 2 — E-Mail',           type:'writing',         minW:80, maxW:100},
    ],
  },
  B2:{
    hoeren:[
      {teil:1,label:'Teil 1 — Radiosendung',     type:'multiple_choice', count:5,  audio:true},
      {teil:2,label:'Teil 2 — Vortrag',          type:'true_false',      count:8,  audio:true},
      {teil:3,label:'Teil 3 — Diskussion',       type:'matching',        count:5,  audio:true},
    ],
    lesen:[
      {teil:1,label:'Teil 1 — Langer Text',      type:'multiple_choice', count:5,  images:1},
      {teil:2,label:'Teil 2 — Textzuordnung',    type:'matching',        count:8,  images:1},
      {teil:3,label:'Teil 3 — Zeitschriftenartikel',type:'gap_fill',     count:6,  images:1,wordList:true},
      {teil:4,label:'Teil 4 — Grammatiktext',    type:'gap_fill',        count:8,  images:1,wordList:true},
    ],
    schreiben:[
      {teil:1,label:'Teil 1 — Erörterung',       type:'writing',         minW:250,maxW:300},
      {teil:2,label:'Teil 2 — Formeller Brief',  type:'writing',         minW:180,maxW:200},
    ],
  },
};

const MC_OPTS = ['a','b','c','d'];
const TF_OPTS = ['richtig','falsch'];

// ── Answer key row ─────────────────────────────────────────
function AnswerRow({ qNum, type, wordList, value, onChange }) {
  const opts = type==='gap_fill' ? (wordList||[]) : type==='multiple_choice' ? MC_OPTS : TF_OPTS;
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-400 w-4 text-right">{qNum}.</span>
      <select value={value||''} onChange={e=>onChange(qNum,e.target.value)}
        className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded-lg bg-white focus:border-brand-500 focus:outline-none">
        <option value="">—</option>
        {opts.map(o=><option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

export default function ContentPage() {
  const { addToast } = useToast();
  const [tab, setTab]     = useState('list');
  const [exams, setExams] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [togglingId, setToggling] = useState(null);

  // Form state
  const [title, setTitle]   = useState('');
  const [level, setLevel]   = useState('A1');
  // sections[secType][teil] = { instructions, wordList, writingPrompt, minWords, maxWords, answers:{qNum:val} }
  const [sData, setSData]   = useState({});
  const [fileMap, setFileMap] = useState({}); // fieldName → File

  useEffect(() => {
    API.get('/exams/admin/list')
      .then(({data}) => setExams(data.exams||[]))
      .catch(()=>{})
      .finally(()=>setLoading(false));
  }, []);

  const struct = GOETHE_STRUCTURE[level] || {};

  const setField = (sec, teil, key, val) =>
    setSData(p=>({...p,[sec]:{...p[sec],[teil]:{...(p[sec]?.[teil]||{}),[key]:val}}}));

  const setAnswer = (sec, teil, qNum, val) => {
    setSData(p=>({...p,[sec]:{...p[sec],[teil]:{...(p[sec]?.[teil]||{}),answers:{...(p[sec]?.[teil]?.answers||{}),[qNum]:val}}}}));
  };

  const handleFile = (fieldName, file) => setFileMap(p=>({...p,[fieldName]:file}));

  const handleSubmit = async () => {
    if (!title.trim()) { addToast('Title required','error'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('level', level);

      const sectionsJson = {};
      for (const [sec, teilMap] of Object.entries(sData)) {
        sectionsJson[sec] = Object.entries(teilMap).map(([teil, d])=>({
          teil: parseInt(teil), instructions: d.instructions||null,
          wordList: d.wordList||null, writingPrompt: d.writingPrompt||null,
          minWords: d.minWords||null, maxWords: d.maxWords||null,
          answers: d.answers||{},
        }));
      }
      fd.append('sections', JSON.stringify(sectionsJson));

      for (const [fn, f] of Object.entries(fileMap)) {
        if (f instanceof File) fd.append(fn, f, f.name);
      }

      const {data} = await API.post('/exams', fd, {headers:{'Content-Type':'multipart/form-data'}});
      addToast(`Exam "${title}" saved (Draft, ID:${data.examId})`, 'success');
      setTitle(''); setSData({}); setFileMap({});
      const {data:list} = await API.get('/exams/admin/list');
      setExams(list.exams||[]);
      setTab('list');
    } catch(err) { addToast(err.message||'Failed','error'); }
    finally { setSaving(false); }
  };

  const togglePublish = async (id) => {
    setToggling(id);
    try {
      const {data} = await API.patch(`/exams/${id}/publish`);
      setExams(p=>p.map(e=>e.id===id?{...e,status:data.status}:e));
      addToast(`Exam ${data.status}`,'success');
    } catch(err){addToast(err.message||'Failed','error');}
    finally{setToggling(null);}
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <SectionHeader title="Exam Content" subtitle="Upload and manage Goethe-style mock exams" />
        <div className="flex gap-1 p-1 bg-surface rounded-xl">
          {['list','upload'].map(t=>(
            <button key={t} onClick={()=>setTab(t)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all
                ${tab===t?'bg-white text-brand-500 shadow-sm':'text-gray-500 hover:text-gray-700'}`}>
              {t==='upload'?'+ Upload Exam':'Exams List'}
            </button>
          ))}
        </div>
      </div>

      {/* ── LIST ──────────────────────────────────────────────── */}
      {tab==='list' && (
        <Card className="overflow-hidden">
          {loading ? <div className="flex justify-center py-12"><Spinner size="lg" className="text-brand-500"/></div>
          : exams.length===0 ? (
            <div className="text-center py-12">
              <p className="text-4xl mb-3">📝</p>
              <p className="font-bold text-de-black mb-2">No exams yet</p>
              <Button variant="primary" onClick={()=>setTab('upload')}>+ Upload First Exam</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface border-b border-gray-200">
                  <tr>{['Title','Level','Sections','Status','Created','Action'].map(h=>(
                    <th key={h} className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {exams.map(e=>(
                    <tr key={e.id} className="border-b border-gray-50 hover:bg-surface">
                      <td className="py-3 px-4 font-semibold text-de-black text-xs">{e.title}</td>
                      <td className="py-3 px-4"><Badge variant="gold">{e.level}</Badge></td>
                      <td className="py-3 px-4 text-xs text-gray-500">{e.section_count||0}</td>
                      <td className="py-3 px-4">
                        <Badge variant={e.status==='published'?'completed':'locked'}>{e.status==='published'?'✓ Live':'Draft'}</Badge>
                      </td>
                      <td className="py-3 px-4 text-xs text-gray-400">{new Date(e.created_at).toLocaleDateString('en-GB')}</td>
                      <td className="py-3 px-4">
                        <Button size="sm" variant={e.status==='published'?'secondary':'primary'}
                          loading={togglingId===e.id} onClick={()=>togglePublish(e.id)}>
                          {e.status==='published'?'Unpublish':'Publish'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ── UPLOAD ────────────────────────────────────────────── */}
      {tab==='upload' && (
        <div className="space-y-6">
          {/* Exam info */}
          <Card className="p-6" accent="red">
            <h3 className="text-sm font-bold text-de-black mb-4">Exam Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-de-black mb-1.5">Exam Title</label>
                <input type="text" value={title} onChange={e=>setTitle(e.target.value)}
                  placeholder="e.g. A1 Mock Exam January 2025"
                  className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white"/>
              </div>
              <div>
                <label className="block text-sm font-bold text-de-black mb-1.5">CEFR Level</label>
                <select value={level} onChange={e=>{setLevel(e.target.value);setSData({});setFileMap({});}}
                  className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white">
                  {['A1','A2','B1','B2'].map(l=><option key={l} value={l}>Level {l}</option>)}
                </select>
              </div>
            </div>
          </Card>

          {/* Per-section forms */}
          {[['hoeren','🎧','Hören'],['lesen','📖','Lesen'],['schreiben','✍️','Schreiben']].map(([sec,icon,label])=>(
            <Card key={sec} className="p-6">
              <h3 className="text-base font-bold text-de-black mb-4 flex items-center gap-2">
                {icon} {label}
                <Badge variant="default" size="xs">{(struct[sec]||[]).length} Teile</Badge>
              </h3>

              <div className="space-y-5">
                {(struct[sec]||[]).map(t=>(
                  <div key={t.teil} className="border border-gray-200 rounded-xl p-4 bg-surface">
                    {/* Teil header */}
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-bold text-de-black">{t.label}</p>
                      <span className="text-xs bg-white border border-gray-200 px-2 py-0.5 rounded-full text-gray-500 capitalize">
                        {t.type.replace('_',' ')} {t.count?`· ${t.count}q`:''}
                      </span>
                    </div>

                    {/* Instructions */}
                    <input type="text" placeholder="Instructions for students (optional)"
                      onChange={e=>setField(sec,t.teil,'instructions',e.target.value)}
                      className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-brand-500 focus:outline-none mb-3 bg-white"/>

                    {/* Audio */}
                    {t.audio && (
                      <div className="mb-3">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">🎧 Audio (MP3/WAV)</label>
                        <input type="file" accept="audio/*"
                          onChange={e=>handleFile(`audio_${sec}_${t.teil}`, e.target.files[0])}
                          className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-brand-50 file:text-brand-600"/>
                      </div>
                    )}

                    {/* Images */}
                    {t.images && (
                      <div className="mb-3">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                          🖼️ Image{t.images>1?`s (up to ${t.images})`:''}
                        </label>
                        <input type="file" accept="image/*" multiple={t.images>1}
                          onChange={e=>Array.from(e.target.files).slice(0,t.images).forEach((f,i)=>handleFile(`images_${sec}_${t.teil}_${i}`,f))}
                          className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-600"/>
                      </div>
                    )}

                    {/* Word list */}
                    {t.wordList && (
                      <div className="mb-3">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Word list (comma-separated)</label>
                        <input type="text" placeholder="Haus, Auto, Schule, Arbeit, Familie"
                          onChange={e=>setField(sec,t.teil,'wordList',e.target.value.split(',').map(w=>w.trim()).filter(Boolean))}
                          className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-brand-500 focus:outline-none bg-white"/>
                      </div>
                    )}

                    {/* Writing prompt */}
                    {t.type==='writing' && (
                      <div className="mb-3 space-y-2">
                        <label className="block text-xs font-semibold text-gray-600">Writing prompt (German)</label>
                        <textarea rows={3} placeholder="Schreiben Sie..."
                          onChange={e=>setField(sec,t.teil,'writingPrompt',e.target.value)}
                          className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-brand-500 focus:outline-none resize-none bg-white"/>
                        <div className="flex gap-3">
                          {[['Min words',t.minW,'minWords'],['Max words',t.maxW,'maxWords']].map(([lbl,def,key])=>(
                            <div key={key}>
                              <label className="block text-xs text-gray-500 mb-1">{lbl}</label>
                              <input type="number" defaultValue={def||80}
                                onChange={e=>setField(sec,t.teil,key,parseInt(e.target.value))}
                                className="w-24 px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:border-brand-500 focus:outline-none bg-white"/>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Answer key */}
                    {t.count>0 && t.type!=='form_fill' && t.type!=='writing' && (
                      <div className="mt-2">
                        <p className="text-xs font-bold text-gray-600 mb-2 uppercase tracking-wide">Answer Key</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1.5">
                          {Array.from({length:t.count},(_,i)=>(
                            <AnswerRow key={i+1} qNum={i+1} type={t.type}
                              wordList={sData[sec]?.[t.teil]?.wordList||[]}
                              value={sData[sec]?.[t.teil]?.answers?.[i+1]||''}
                              onChange={(n,v)=>setAnswer(sec,t.teil,n,v)}/>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          ))}

          {/* Submit bar */}
          <div className="sticky bottom-4 flex justify-end">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-4 flex items-center gap-4">
              <p className="text-sm text-gray-600">
                Level <strong>{level}</strong> — {(struct.hoeren?.length||0)+(struct.lesen?.length||0)+(struct.schreiben?.length||0)} Teile total
              </p>
              <Button variant="ghost" onClick={()=>setTab('list')}>Cancel</Button>
              <Button variant="primary" size="lg" loading={saving} onClick={handleSubmit}>
                Save as Draft
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
